/**
 * Types pour la gestion des commandes
 */

export interface OrderLine {
  lineId: string;
  articleId: string;
  articleReference: string;
  articleName: string;
  articleImage?: string;
  quantity: number;
  unitPriceHT: number;
  tvaRate: number;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
}

export type OrderStatus =
  | 'pending_confirmation'  // Commande en attente de confirmation
  | 'confirmed'             // Confirmée par le commercial
  | 'preparing'             // En cours de préparation
  | 'shipped'               // Expédiée
  | 'delivered'             // Livrée
  | 'cancelled';            // Annulée

export type OrderSource = 'frontoffice' | 'backoffice';

/**
 * Mode d'acheminement choisi par l'acheteur au checkout.
 * Propriete de la COMMANDE (pas de l'article) : disponible pour tous les types d'article.
 * `undefined` se lit comme `'delivery'` : les commandes creees avant l'ajout de ce champ
 * restent des livraisons. Ne jamais rendre ce champ obligatoire (retro-compat).
 */
export type OrderDeliveryMode = 'delivery' | 'pickup';

export interface OrderDelivery {
  address: string;
  city: string;
  postalCode?: string;
  country?: string;
  recipientName?: string;
  phone?: string;
  deliveryFee?: number;
  deliveryFeeType?: 'included' | 'cash_on_delivery' | 'free';
  deliveryFeeCOD?: number;
  estimatedDate?: string;
  estimatedDeliveryDate?: string;
  instructions?: string;
  trackingNumber?: string;
  carrier?: string;
  driverPhone?: string;
  deliveryCompany?: string;
}

export interface Order {
  orderId: string;
  orderNumber: string;
  source: OrderSource;
  status: OrderStatus;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  lines: OrderLine[];
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  delivery?: OrderDelivery;
  // Mode d'acheminement choisi au checkout (undefined === 'delivery')
  deliveryMode?: OrderDeliveryMode;
  createdAt: string;
  confirmedAt?: string;
  confirmedBy?: string;
  confirmedByName?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelReason?: string;
  invoiceId?: string;
  notes?: string;
}

export interface CreateOrderInput {
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  delivery?: {
    address: string;
    city: string;
    postalCode?: string;
    country?: string;
    deliveryFee?: number;
    estimatedDate?: string;
  };
  // undefined === 'delivery' (comportement historique)
  deliveryMode?: OrderDeliveryMode;
  notes?: string;
}

export interface UpdateOrderStatusInput {
  status: OrderStatus;
  notes?: string;
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending_confirmation: 'En attente de confirmation',
  confirmed: 'Confirmée',
  preparing: 'En cours de préparation',
  shipped: 'En cours de livraison',
  delivered: 'Livrée',
  cancelled: 'Annulée',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  pending_confirmation: '#f59e0b',  // Orange
  confirmed: '#3b82f6',             // Blue
  preparing: '#8b5cf6',             // Purple
  shipped: '#06b6d4',               // Cyan
  delivered: '#10b981',             // Green
  cancelled: '#ef4444',             // Red
};
